import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, MapPin, Play, Sparkles } from "lucide-react";
import { IMG, VIDEOS, STATS } from "../../mock";
import { useParallax } from "../../lib/useReveal";
import VideoModal from "../VideoModal";
import { Counter } from "../common";

const WORDS = ["35+ Years of", "Volunteering", "Overseas"];

export default function Hero() {
  const [video, setVideo] = useState(false);
  const offset = useParallax(0.22);

  return (
    <>
    <section className="relative isolate min-h-[100svh] overflow-hidden bg-ivi-slate">
      <div
        className="absolute inset-0 -z-10 scale-110"
        style={{ transform: `translate3d(0, ${offset}px, 0) scale(1.12)` }}
      >
        <img
          src={IMG.heroBeach}
          alt="Aerial view of a tropical island beach"
          className="h-full w-full object-cover"
        />
      </div>
      {/* Layered depth: dark scrim so white text always reads */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-ivi-slate/80 via-ivi-slate/45 to-ivi-slate/85" />
      <div className="grain absolute inset-0 -z-10" />

      <div className="mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-center px-5 pb-32 pt-32 lg:px-8">
        <div className="max-w-3xl">
          <span className="inline-flex animate-[float-slow_6s_ease-in-out_infinite] items-center gap-2 rounded-full bg-white/12 px-4 py-2 font-heading text-[12px] font-bold uppercase tracking-[0.2em] text-white ring-1 ring-white/25 backdrop-blur-md">
            <Sparkles className="h-3.5 w-3.5 text-ivi-lime" />
            Charity &amp; Non-Profit Since 1989
          </span>

          <h1 className="mt-7 font-heading text-[46px] font-extrabold leading-[1.04] tracking-[-0.03em] text-white sm:text-[68px] lg:text-[80px]">
            {WORDS.map((w, i) => (
              <span key={w} className="block overflow-hidden pb-1">
                <HeroWord text={w} delay={i * 150} accent={i === 2} />
              </span>
            ))}
          </h1>

          <p className="mt-7 max-w-xl text-[18px] leading-relaxed text-white/85">
            Real projects, real communities, real change. Join 32,000+ volunteers who have taught,
            healed, built and protected wildlife alongside local partners in over 20 countries.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/projects/volunteer-in-bali"
              className="group inline-flex items-center gap-2 rounded-full bg-ivi-green px-8 py-4 font-heading text-[15px] font-bold text-white shadow-[0_18px_40px_-16px_rgba(118,188,67,1)] transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-ivi-greenDark"
            >
              <MapPin className="h-4 w-4" />
              Explore Bali projects
            </Link>
            <button
              type="button"
              onClick={() => setVideo(true)}
              className="group inline-flex items-center gap-3 rounded-full border border-white/30 bg-white/10 px-6 py-3 font-heading text-[15px] font-bold text-white backdrop-blur-md transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-white/20"
            >
              <span className="grid h-9 w-9 place-items-center rounded-full bg-white text-ivi-greenDark transition-transform duration-200 group-hover:scale-110">
                <Play className="ml-0.5 h-4 w-4 fill-current" />
              </span>
              Watch the Bali story
            </button>
          </div>
        </div>
      </div>

      <a
        href="#story"
        aria-label="Scroll down"
        className="absolute bottom-[12%] left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-white/70 transition-colors duration-200 hover:text-white lg:flex"
      >
        <span className="text-[11px] font-semibold uppercase tracking-[0.3em]">Scroll</span>
        <ChevronDown className="h-5 w-5 animate-bounce" />
      </a>

      <VideoModal
        open={video}
        onOpenChange={setVideo}
        videoId={VIDEOS.bali.id}
        title={VIDEOS.bali.title}
      />
    </section>

    {/* Stat bar: lives outside the hero so nothing gets clipped */}
    <div className="relative z-30 -mt-[62px] px-5 lg:px-8">
      <div className="mx-auto grid max-w-[1180px] grid-cols-2 gap-px overflow-hidden rounded-3xl bg-white shadow-[0_30px_70px_-30px_rgba(24,40,32,0.45)] ring-1 ring-ivi-mintDeep md:grid-cols-4">
        {STATS.map((s) => (
          <div key={s.label} className="bg-white px-6 py-7 text-center">
            <p className="font-heading text-[30px] font-extrabold leading-none text-ivi-greenDark sm:text-[34px]">
              <Counter value={s.value} suffix={s.suffix} />
            </p>
            <p className="mt-2 text-[13px] font-semibold uppercase tracking-[0.14em] text-ivi-slateSoft">
              {s.label}
            </p>
          </div>
        ))}
      </div>
    </div>
    </>
  );
}

function HeroWord({ text, delay, accent }) {
  return (
    <span
      className={accent ? "text-ivi-lime" : ""}
      style={{
        display: "inline-block",
        animation: `heroUp 0.9s cubic-bezier(0.22,1,0.36,1) ${delay}ms both`,
      }}
    >
      {text}
    </span>
  );
}
